import React, { useState, useEffect } from "react";
import styles from "./BrandAnalyticsPage.module.scss";
import { Helmet } from "react-helmet";
import {
  DatePicker,
  Input,
  Tooltip,
  Select,
  InputNumber,
  Button,
  ConfigProvider,
} from "antd";
import {
  SearchOutlined,
  QuestionCircleOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AsyncPaginate } from "react-select-async-paginate";
import BrandAnalyticsDisplay from "../../../components/site/BrandAnalytics/BrandAnalyticsDisplay";
import {
  asyncPaginateStyles,
  loadBrandOptions,
  loadCategoryOptions,
  loadSubcategoryOptions,
  loadSubgroupOptions,
  loadNominationOptions,
} from "../../../utils/getProductListCardDetails";

import ProductAnalyticsFilters from "../../../components/site/ProductAnalyticsFilters/ProductAnalyticsFilters";
import { useUsageNotification } from "../../../hooks/useUsageNotification";

import dayjs from "dayjs";
import "dayjs/locale/ru";
import ruRU from "antd/es/locale/ru_RU";

// Set dayjs to use Russian locale
dayjs.locale("ru");

const { Option } = Select;

const BrandAnalyticsPage = () => {
  const dateCutoff =
    process.env.REACT_APP_DATE_CUTOFF === "one_month" ? 1 : 2;
  const [fromDate, setFromDate] = useState(() =>
    dayjs().subtract(dateCutoff, "month")
  );
  const [toDate, setToDate] = useState(() =>
    dayjs().subtract(dateCutoff, "month")
  );
  const [searchValue, setSearchValue] = useState("");

  // Filter states
  const [selectedName, setSelectedName] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedSubgroup, setSelectedSubgroup] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [sellersMin, setSellersMin] = useState(null);
  const [sellersMax, setSellersMax] = useState(null);
  const [ordersMin, setOrdersMin] = useState(null);
  const [ordersMax, setOrdersMax] = useState(null);
  const [revenueMin, setRevenueMin] = useState(null);
  const [revenueMax, setRevenueMax] = useState(null);

  // Brand selection state for AsyncPaginate
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Search trigger state
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // When a brand name is clicked we clear filters and drill into that single
  // brand. The fetch is deferred to an effect so it reads the freshly-committed
  // state instead of the stale render closure.
  const [pendingBrandDetail, setPendingBrandDetail] = useState(null);

  // Brand analytics data state
  const [brandAnalyticsData, setBrandAnalyticsData] = useState(null);
  const [brandAnalyticsLoading, setBrandAnalyticsLoading] = useState(false);
  const [brandAnalyticsError, setBrandAnalyticsError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Sorting state
  const [sorting, setSorting] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Usage notification hook
  const { showUsageInfo, handleApiError } =
    useUsageNotification("brand_analytics");

  // Handle category change and clear dependent dropdowns
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setSelectedSubcategory(null);
    setSelectedSubgroup(null);
    setSelectedType(null);
  };

  // Handle subcategory change and clear dependent dropdown
  const handleSubcategoryChange = (selectedOption) => {
    setSelectedSubcategory(selectedOption);
    setSelectedSubgroup(null);
    setSelectedType(null);
  };

  const handleSubgroupChange = (selectedOption) => {
    setSelectedSubgroup(selectedOption);
    setSelectedType(null);
  };

  // Fetch brand analytics data
  const fetchBrandAnalytics = async (page = currentPage, sortFieldParam = null, sortDirectionParam = null) => {
    const token = localStorage?.getItem("token");
    if (!token) {
      setBrandAnalyticsError("Токен аутентификации не найден");
      return;
    }

    setBrandAnalyticsLoading(true);
    setBrandAnalyticsError(null);

    try {
      const params = {};

      // Date range
      if (fromDate) params.from_date = fromDate.format("YYYY-MM");
      if (toDate) params.to_date = toDate.format("YYYY-MM");

      // Filters - Handle multi-select (arrays) and single-select
      // Send as arrays to properly handle commas in values
      if (selectedBrand) {
        params.brand = Array.isArray(selectedBrand)
          ? selectedBrand.map((b) => b.value)
          : [selectedBrand.value];
      }
      if (selectedCategory) {
        params.root_category = Array.isArray(selectedCategory)
          ? selectedCategory.map((c) => c.value)
          : [selectedCategory.value];
      }
      if (selectedSubcategory) {
        params.category_2 = Array.isArray(selectedSubcategory)
          ? selectedSubcategory.map((c) => c.value)
          : [selectedSubcategory.value];
      }
      if (selectedSubgroup) {
        params.category_3 = Array.isArray(selectedSubgroup)
          ? selectedSubgroup.map((c) => c.value)
          : [selectedSubgroup.value];
      }
      if (selectedType) {
        params.category_4 = Array.isArray(selectedType)
          ? selectedType.map((c) => c.value)
          : [selectedType.value];
      }

      // Price range
      if (priceMin !== null) params.min_price = priceMin;
      if (priceMax !== null) params.max_price = priceMax;

      // Sellers range
      if (sellersMin !== null) params.min_sellers_count = sellersMin;
      if (sellersMax !== null) params.max_sellers_count = sellersMax;

      // Orders range
      if (ordersMin !== null) params.min_sells_count = ordersMin;
      if (ordersMax !== null) params.max_sells_count = ordersMax;

      // Revenue range
      if (revenueMin !== null) params.min_gmv = revenueMin;
      if (revenueMax !== null) params.max_gmv = revenueMax;

      // Pagination
      params.page = page;
      params.page_size = pageSize;

      // Sorting
      const currentSortField = sortFieldParam !== null ? sortFieldParam : sortField;
      const currentSortDirection = sortDirectionParam !== null ? sortDirectionParam : sortDirection;
      
      if (currentSortField) {
        params.sorting = `${currentSortField}_${currentSortDirection}`;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_API}/products-analytics/brand-analytics/`,
        {
          params,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, item));
              } else if (value !== null && value !== undefined) {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      setBrandAnalyticsData(response.data);
      setCurrentPage(page);

      // Show usage notification after successful search
      showUsageInfo();
    } catch (error) {
      console.error("Error fetching brand analytics:", error);

      // Clear previous data when error occurs
      setBrandAnalyticsData(null);

      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      if (status === 401) {
        setBrandAnalyticsError(
          detail || "Сессия истекла. Пожалуйста, войдите заново."
        );
      } else if (status === 403 && error.response.data) {
        // Handle usage limit errors with their dedicated notification first
        const isLimitError = handleApiError(error.response.data);
        if (!isLimitError) {
          setBrandAnalyticsError(
            detail ||
              "У вас нет доступа к этой функции. Обратитесь в техподдержку."
          );
        }
      } else {
        setBrandAnalyticsError("Ошибка при загрузке аналитики бренда");
      }
    } finally {
      setBrandAnalyticsLoading(false);
      handleSearchComplete();
    }
  };

  const handleClearSearch = () => {
    setSearchValue("");

    setSelectedName(null);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedSubgroup(null);
    setSelectedType(null);
    setPriceMin(null);
    setPriceMax(null);
    setSellersMin(null);
    setSellersMax(null);
    setOrdersMin(null);
    setOrdersMax(null);
    setRevenueMin(null);
    setRevenueMax(null);
    setSorting(null);
    setSelectedBrand(null);
    setSortField(null);
    setSortDirection("asc");

    setSearchTrigger((prev) => prev + 1);
    setIsSearching(true);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page
    setIsSearching(true);
    fetchBrandAnalytics(1);
  };

  const handleFilterSearch = () => {
    console.log("Поиск button was triggered");
    setCurrentPage(1); // Reset to first page
    setIsSearching(true);
    fetchBrandAnalytics(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchBrandAnalytics(newPage);
  };

  const handleSort = (field) => {
    let newDirection = "asc";

    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    }

    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);

    fetchBrandAnalytics(1, field, newDirection);
  };

  const handleSearchComplete = () => {
    setIsSearching(false);
    setBrandAnalyticsLoading(false);
  };

  const handleBrandFilter = (brandName) => {
    // Clear all other filters and drill into the single clicked brand.
    setSearchValue("");
    setSelectedName(null);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedSubgroup(null);
    setSelectedType(null);
    setPriceMin(null);
    setPriceMax(null);
    setSellersMin(null);
    setSellersMax(null);
    setOrdersMin(null);
    setOrdersMax(null);
    setRevenueMin(null);
    setRevenueMax(null);
    setSorting(null);
    setSortField(null);
    setSortDirection("asc");

    // Set the brand filter. Must be an ARRAY: the Brand select is `isMulti`
    // and ProductAnalyticsFilters maps over selectedBrand — an object here
    // throws during render and blanks the page.
    setSelectedBrand([{ value: brandName, label: brandName }]);

    // Reset pagination and defer the fetch until the cleared state commits
    // (see pendingBrandDetail effect) so we don't send stale filter values.
    setCurrentPage(1);
    setIsSearching(true);
    setPendingBrandDetail(brandName);
  };

  // Runs after handleBrandFilter's state updates commit, so fetchBrandAnalytics
  // reads the fresh single-brand selection with all other filters cleared.
  useEffect(() => {
    if (pendingBrandDetail) {
      fetchBrandAnalytics(1);
      setPendingBrandDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingBrandDetail]);

  const handleFromDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      setFromDate(date);
      // Ensure fromDate is not after toDate
      if (toDate && date.isAfter(toDate)) {
        setToDate(date);
      }
    }
  };

  const handleToDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      setToDate(date);
      // Ensure toDate is not before fromDate
      if (fromDate && date.isBefore(fromDate)) {
        setFromDate(date);
      }
    }
  };

  const handleYouTubeClick = () => {
    // Replace with actual YouTube video URL
    window.open("https://youtu.be/xbBE0yOeMzg", "_blank");
  };

  return (
    <>
      <Helmet>
        <title>Аналитика брендов</title>
      </Helmet>

      <div className="crm demo6 mb-25">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb-main">
                <h4 className="text-capitalize breadcrumb-title">
                  Аналитика брендов
                </h4>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              {/* Controls Section */}

              <ProductAnalyticsFilters
                // Date range
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                // Search
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                // Selection filters
                selectedBrand={selectedBrand}
                onBrandChange={setSelectedBrand}
                selectedName={selectedName}
                onNameChange={setSelectedName}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                selectedSubcategory={selectedSubcategory}
                onSubcategoryChange={handleSubcategoryChange}
                selectedSubgroup={selectedSubgroup}
                onSubgroupChange={handleSubgroupChange}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                // Range filters
                priceMin={priceMin}
                priceMax={priceMax}
                onPriceMinChange={setPriceMin}
                onPriceMaxChange={setPriceMax}
                sellersMin={sellersMin}
                sellersMax={sellersMax}
                onSellersMinChange={setSellersMin}
                onSellersMaxChange={setSellersMax}
                ordersMin={ordersMin}
                ordersMax={ordersMax}
                onOrdersMinChange={setOrdersMin}
                onOrdersMaxChange={setOrdersMax}
                revenueMin={revenueMin}
                revenueMax={revenueMax}
                onRevenueMinChange={setRevenueMin}
                onRevenueMaxChange={setRevenueMax}
                // Actions
                onFilterSearch={handleFilterSearch}
                onYouTubeClick={handleYouTubeClick}
              />
              {/* Content Section */}
              <BrandAnalyticsDisplay
                brandData={brandAnalyticsData}
                isLoading={brandAnalyticsLoading || isSearching}
                error={brandAnalyticsError}
                to_date={toDate.format("YYYY-MM")}
                onPageChange={handlePageChange}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onBrandClick={handleBrandFilter}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BrandAnalyticsPage;
